import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Column } from 'styled/alignment/Column';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import Modal from 'components/_UI/Modal';
import Fieldset from 'components/_UI/Fieldset';
import Button from 'components/_UI/Button';
import Text from 'components/_UI/Text';
import Loader from 'components/_UI/Loader';
import CurrencyInputField from 'components/_UI/CurrencyInputField';
import Select from 'react-select';
import { X } from 'lucide-react';
import menuApi from 'api/requests/menu';
import { panelAccent } from 'theme/panelAccent';
import {
    createInventoryItem,
    updateInventoryItem,
    getInventoryItemTechSheets,
    syncInventoryItemTechSheets
} from 'api/requests/inventory';

const unitOptions = [
    { value: 'un',  label: 'Unidade (un)' },
    { value: 'kg',  label: 'Quilograma (kg)' },
    { value: 'g',   label: 'Grama (g)' },
    { value: 'l',   label: 'Litro (l)' },
    { value: 'ml',  label: 'Mililitro (ml)' },
    { value: 'pct', label: 'Pacote (pct)' },
    { value: 'cx',  label: 'Caixa (cx)' },
];

export default function InventoryItemModal({
    isOpen,
    onClose,
    establishmentSlug,
    itemToEdit,
    onSuccess,
    toast
}) {
    const isEditing = !!itemToEdit;

    // Dados do insumo
    const [name, setName]       = useState('');
    const [unit, setUnit]       = useState(unitOptions[0]);
    const [minStock, setMinStock] = useState('0');

    // Dados da compra/entrada (presentes em criação E edição)
    const [purchasedQuantity, setPurchasedQuantity] = useState('');
    const [totalPaid, setTotalPaid]                 = useState('');
    const [purchaseDate, setPurchaseDate]           = useState('');
    const [supplier, setSupplier]                   = useState('');
    const [note, setNote]                           = useState('');

    // Produtos e adicionais que usam este insumo (ficha técnica)
    const [allProducts, setAllProducts]         = useState([]);
    const [allOptionGroups, setAllOptionGroups] = useState([]);
    const [techSheets, setTechSheets]           = useState([]);
    const [selectedItem, setSelectedItem]       = useState(null);
    const [itemQuantity, setItemQuantity]       = useState('');

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        menuApi.getProducts(establishmentSlug)
            .then(res => setAllProducts(res.data.data || []))
            .catch(() => {});
        menuApi.getOptionGroups(establishmentSlug)
            .then(res => setAllOptionGroups(res.data.data || []))
            .catch(() => {});
    }, [establishmentSlug]);

    useEffect(() => {
        if (isOpen) {
            setName(itemToEdit?.name || '');
            const matchedUnit = unitOptions.find(opt => opt.value === itemToEdit?.unit_of_measure);
            setUnit(matchedUnit || unitOptions[0]);
            setMinStock(itemToEdit?.min_stock_alert ? String(itemToEdit.min_stock_alert) : '0');

            // Sempre limpa os campos de compra ao abrir
            setPurchasedQuantity('');
            setTotalPaid('');
            setPurchaseDate('');
            setSupplier('');
            setNote('');

            setSelectedItem(null);
            setItemQuantity('');
            if (itemToEdit) {
                getInventoryItemTechSheets(establishmentSlug, itemToEdit.id)
                    .then(sheets => setTechSheets(
                        (sheets || []).map(s => ({
                            type: s.type,
                            ...(s.type === 'product'
                                ? { menu_product_id: s.menu_product_id }
                                : { menu_option_id: s.menu_option_id }),
                            quantity_used: Number(s.quantity_used)
                        }))
                    ))
                    .catch(() => setTechSheets([]));
            } else {
                setTechSheets([]);
            }
        }
    }, [isOpen, itemToEdit, establishmentSlug]);

    function addTechSheetRow() {
        if (!selectedItem) return;
        const qty = Number(itemQuantity);
        if (!qty || qty <= 0) {
            toast.error('Informe uma quantidade válida.');
            return;
        }
        setTechSheets(prev => [...prev, {
            type: selectedItem.type,
            ...(selectedItem.type === 'product'
                ? { menu_product_id: selectedItem.id }
                : { menu_option_id: selectedItem.id }),
            quantity_used: qty,
        }]);
        setSelectedItem(null);
        setItemQuantity('');
    }

    function removeTechSheetRow(type, id) {
        setTechSheets(prev => prev.filter(t => {
            if (t.type !== type) return true;
            return type === 'product' ? t.menu_product_id !== id : t.menu_option_id !== id;
        }));
    }

    const flatOptions = allOptionGroups.flatMap(group =>
        (group.options || []).map(opt => ({ ...opt, groupName: group.name }))
    );

    const productPickerOptions = allProducts
        .filter(p => !techSheets.some(t => t.type === 'product' && t.menu_product_id === p.id))
        .map(p => ({ value: `product-${p.id}`, label: p.name, type: 'product', id: p.id }));

    const optionPickerOptions = flatOptions
        .filter(o => !techSheets.some(t => t.type === 'option' && t.menu_option_id === o.id))
        .map(o => ({ value: `option-${o.id}`, label: `${o.name} (${o.groupName})`, type: 'option', id: o.id }));

    const groupedPickerOptions = [
        { label: 'Produtos', options: productPickerOptions },
        { label: 'Adicionais', options: optionPickerOptions },
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!name) { toast.error('O nome do insumo é obrigatório.'); return; }
        if (!unit)  { toast.error('A unidade de medida é obrigatória.'); return; }

        try {
            setLoading(true);

            const payload = {
                name,
                unit_of_measure: unit.value,
                min_stock_alert: Number(minStock) || 0,
            };

            // Inclui dados de compra/entrada se houver quantidade informada
            if (purchasedQuantity && Number(purchasedQuantity) > 0) {
                payload.purchased_quantity = Number(purchasedQuantity);
                payload.total_paid         = Number(totalPaid) || 0;
                if (purchaseDate) payload.purchase_date = purchaseDate;
                if (supplier)     payload.supplier      = supplier;
                if (note)         payload.note          = note;
            }

            let savedItem;
            if (isEditing) {
                savedItem = await updateInventoryItem(establishmentSlug, itemToEdit.id, payload);
                toast.success('Insumo atualizado com sucesso!');
            } else {
                savedItem = await createInventoryItem(establishmentSlug, payload);
                toast.success('Insumo cadastrado com sucesso!');
            }

            // Sincroniza produtos/adicionais vinculados sempre que editando (permite zerar a lista)
            // ou ao criar com pelo menos um item selecionado
            if (isEditing || techSheets.length > 0) {
                await syncInventoryItemTechSheets(establishmentSlug, savedItem.id, techSheets);
            }

            onSuccess();
            onClose();
        } catch (error) {
            console.error('Error saving item:', error);
            toast.error('Erro ao salvar insumo. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="medium">
            <Container>
                <Text name={'highlight'}>
                    {isEditing ? 'Editar Insumo' : 'Novo Insumo'}
                </Text>

                <FormContainer onSubmit={handleSubmit}>

                    {/* ── Dados do insumo ── */}
                    <SectionLabel>Dados do insumo</SectionLabel>
                    <Row>
                        <Column style={{ flex: 2 }}>
                            <Fieldset
                                label="Nome do Insumo *"
                                layout="squired"
                                placeholder="Ex: Carne Moída, Pão..."
                                value={name}
                                onChangeValue={setName}
                                syncProp
                            />
                        </Column>
                        <ContentFieldStatus style={{ flex: 1 }}>
                            <Text>Unidade de Medida *</Text>
                            <Select
                                placeholder="Selecione"
                                options={unitOptions}
                                value={unit}
                                onChange={setUnit}
                            />
                        </ContentFieldStatus>
                    </Row>

                    <Row style={{ marginTop: 10 }}>
                        <Column style={{ flex: 1 }}>
                            <Fieldset
                                label="Alerta de Estoque Mínimo"
                                layout="squired"
                                inputType="number"
                                placeholder="Ex: 5"
                                value={minStock}
                                onChangeValue={setMinStock}
                                syncProp
                            />
                        </Column>
                        <Column style={{ flex: 2 }} />
                    </Row>

                    {/* ── Produtos e adicionais que usam este insumo ── */}
                    <Divider />
                    <SectionLabel>Produtos e adicionais que usam este insumo</SectionLabel>
                    {techSheets.length > 0 && (
                        <TechSheetList>
                            {techSheets.map(t => {
                                const isProduct = t.type === 'product';
                                const product = isProduct && allProducts.find(p => p.id === t.menu_product_id);
                                const option = !isProduct && flatOptions.find(o => o.id === t.menu_option_id);
                                const rowKey = `${t.type}-${isProduct ? t.menu_product_id : t.menu_option_id}`;
                                let label;
                                if (isProduct) {
                                    label = product
                                        ? `${product.name} — ${t.quantity_used} ${unit?.value || ''}`
                                        : `Produto removido — ${t.quantity_used}`;
                                } else {
                                    label = option
                                        ? `${option.name} (adicional) — ${t.quantity_used} ${unit?.value || ''}`
                                        : `Adicional removido — ${t.quantity_used}`;
                                }
                                return (
                                    <TechSheetRow key={rowKey}>
                                        <TechSheetInfo>{label}</TechSheetInfo>
                                        <RemoveRowBtn
                                            type='button'
                                            onClick={() => removeTechSheetRow(t.type, isProduct ? t.menu_product_id : t.menu_option_id)}
                                        >
                                            <X size={14} />
                                        </RemoveRowBtn>
                                    </TechSheetRow>
                                );
                            })}
                        </TechSheetList>
                    )}
                    <TechSheetAddRow>
                        <ReactSelectWrapper>
                            <Select
                                placeholder='Selecionar produto ou adicional'
                                options={groupedPickerOptions}
                                value={selectedItem}
                                onChange={setSelectedItem}
                            />
                        </ReactSelectWrapper>
                        <QtyInput
                            type='number'
                            step='0.0001'
                            min='0'
                            placeholder={`Qtd. (${unit?.value || ''})`}
                            value={itemQuantity}
                            onChange={e => setItemQuantity(e.target.value)}
                        />
                        <AddRowBtn type='button' onClick={addTechSheetRow}>Adicionar</AddRowBtn>
                    </TechSheetAddRow>

                    {/* ── Entrada de estoque ── */}
                    <Divider />
                    <SectionLabel>
                        {isEditing ? 'Registrar nova entrada (opcional)' : 'Estoque inicial / Primeira compra (opcional)'}
                    </SectionLabel>

                    <Row>
                        <Column style={{ flex: 1 }}>
                            <Fieldset
                                label="Quantidade"
                                layout="squired"
                                inputType="number"
                                placeholder="Ex: 10"
                                value={purchasedQuantity}
                                onChangeValue={setPurchasedQuantity}
                            />
                        </Column>
                        <Column style={{ flex: 1 }}>
                            <CurrencyInputField
                                label="Valor Total Pago (R$)"
                                layout="squired"
                                value={totalPaid}
                                onChange={(val) => setTotalPaid(val ? val.replace('.', '') : '')}
                            />
                        </Column>
                    </Row>

                    <Row style={{ marginTop: 10 }}>
                        <Column style={{ flex: 1 }}>
                            <Fieldset
                                label="Fornecedor"
                                layout="squired"
                                placeholder="Nome do Fornecedor"
                                value={supplier}
                                onChangeValue={setSupplier}
                            />
                        </Column>
                        <Column style={{ flex: 1 }}>
                            <Fieldset
                                label="Data da Compra"
                                layout="squired"
                                inputType="date"
                                value={purchaseDate}
                                onChangeValue={setPurchaseDate}
                            />
                        </Column>
                    </Row>

                    <Row style={{ marginTop: 10 }}>
                        <Column style={{ flex: 1 }}>
                            <Fieldset
                                label="Observação"
                                layout="squired"
                                placeholder="Detalhes adicionais..."
                                value={note}
                                onChangeValue={setNote}
                            />
                        </Column>
                    </Row>

                    {/* ── Botões ── */}
                    <ContentButtons>
                        {loading ? (
                            <Loader />
                        ) : (
                            <>
                                <Button
                                    type="button"
                                    variant="light"
                                    category="roundedBorded"
                                    onClick={onClose}
                                >
                                    Cancelar
                                </Button>
                                <Button type="submit">
                                    {isEditing ? 'Salvar Insumo' : 'Cadastrar Insumo'}
                                </Button>
                            </>
                        )}
                    </ContentButtons>
                </FormContainer>
            </Container>
        </Modal>
    );
}

/* ── Styled Components ── */

const Container = styled(Column)`
    width: 100%;
    padding-top: 10px;
    padding-bottom: 20px;
`;

const FormContainer = styled.form`
    text-align: left;
    margin-top: 15px;
`;

const Row = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        align-items: flex-end;
        gap: 15px;
    }
`;

const SectionLabel = styled.p`
    font-size: 12px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin-bottom: 10px;
    margin-top: 4px;
`;

const Divider = styled.div`
    height: 1px;
    background: ${colors.night}15;
    margin: 18px 0 14px;
`;

const ContentFieldStatus = styled(Column)`
    width: 100%;
    & > p {
        font-size: 12px;
        font-weight: 600;
        color: ${colors.nightDark} !important;
        margin-bottom: 4px;
    }
`;

const ContentButtons = styled(Column)`
    margin-top: 25px;
    gap: 10px;
    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        justify-content: flex-end;
    }
`;

const TechSheetList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 10px;
`;

const TechSheetRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 10px;
    background: ${colors.neutralBackground || '#f9f9f9'};
    border: 1px solid ${colors.neutralDark};
`;

const TechSheetInfo = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: ${colors.nightDark};
`;

const RemoveRowBtn = styled.button`
    width: 22px;
    height: 22px;
    border-radius: 50%;
    flex-shrink: 0;
    background: rgba(0, 0, 0, 0.08);
    color: ${colors.night};
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    &:hover { background: ${colors.danger}20; color: ${colors.danger}; }
`;

const TechSheetAddRow = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`;

const ReactSelectWrapper = styled.div`
    flex: 2;
`;

const QtyInput = styled.input`
    flex: 1;
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 12px 14px;
    font-size: 15px;
    outline: none;
    color: ${colors.nightDark};
    background: #fff;
    transition: border-color 0.15s;
    &:focus { border-color: ${panelAccent(colors.occasionalPurple)}; }
    &::placeholder { color: #999; }
`;

const AddRowBtn = styled.button`
    flex-shrink: 0;
    padding: 12px 16px;
    border-radius: 10px;
    border: none;
    background: ${panelAccent(colors.occasionalPurple)};
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s;
    &:hover { opacity: 0.9; }
`;
